<template>
  <div class="mix-selector">
    <!-- Search Section -->
    <div class="mix-search">
      <div class="mix-search__header">
        <div class="search-wrapper">
          <CIcon name="cil-magnifying-glass" size="sm" class="search-icon" />
          <input
            class="form-control"
            v-model="searchTerm"
            placeholder="Buscar produto por nome ou ID..."
            @input="debounceSearch"
            :disabled="isDisabled"
          />
        </div>
        <span class="selected-counter">
          <strong>{{ itemsSelected.length }}</strong> produto{{ itemsSelected.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Loading -->
      <div v-if="searchLoading" class="loading-indicator">
        Buscando...
      </div>

      <!-- Search results -->
      <div v-else-if="searchResults.length > 0" class="produto-list">
        <div class="produto-list-header">
          <div class="pl-check"></div>
          <div class="pl-img">Imagem</div>
          <div class="pl-info">Produto</div>
        </div>
        <div
          v-for="produto in searchResults"
          :key="'sr-' + produto.id"
          class="produto-row"
          :class="{ selected: isSelecionado(produto.id) }"
          @click="toggleProduto(produto)"
        >
          <div class="pl-check">
            <input
              type="checkbox"
              :checked="isSelecionado(produto.id)"
              @click.stop="toggleProduto(produto)"
              :disabled="isDisabled"
            />
          </div>
          <div class="pl-img">
            <div class="pl-thumb">
              <img
                v-if="produto.imagemId"
                :src="getImageUrl(produto)"
                :alt="produto.descComp || produto.descNF"
              />
              <CIcon v-else name="cil-basket" class="thumb-placeholder" />
            </div>
          </div>
          <div class="pl-info">
            <div class="pl-name">{{ produto.descComp || produto.descNF || produto.desc }}</div>
            <div class="pl-id">ID: {{ produto.idExibicao || produto.id }}</div>
          </div>
        </div>
      </div>

      <!-- Empty search state -->
      <div v-else-if="searchTerm && searchTerm.length >= 2 && !searchLoading" class="mix-empty">
        <CIcon name="cil-magnifying-glass" size="xl" class="empty-icon" />
        <p>Nenhum produto encontrado</p>
      </div>

      <!-- No search state -->
      <div v-else-if="!searchTerm && itemsSelected.length === 0" class="mix-empty">
        <CIcon name="cil-basket" size="xl" class="empty-icon" />
        <p>Busque produtos para adicionar ao mix</p>
      </div>
    </div>

    <!-- Selected Products Section -->
    <div v-if="itemsSelected.length > 0" class="mix-selected">
      <div class="mix-selected__label">
        Produtos do mix ({{ itemsSelected.length }})
      </div>
      <div class="mix-list">
        <div class="mix-list-header">
          <div class="ml-thumb-col"></div>
          <div class="ml-info-col">Produto</div>
          <div class="ml-perc-col">% Desconto</div>
          <div class="ml-action-col"></div>
        </div>
        <div
          v-for="(produto, index) in itemsSelected"
          :key="'mix-' + produto.id"
          class="mix-row"
        >
          <div class="mix-row__thumb">
            <img
              v-if="produto.imagemId"
              :src="getImageUrl(produto)"
              :alt="produto.desc"
            />
            <CIcon v-else name="cil-basket" class="thumb-placeholder" />
          </div>
          <div class="mix-row__info">
            <div class="mix-row__name">{{ produto.desc }}</div>
            <div class="mix-row__id">ID: {{ produto.idExibicao || produto.id }}</div>
          </div>
          <div class="mix-row__perc">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              :value="produto.percDesc"
              @input="onPercentageChange(index, $event.target.value)"
              @keydown="['-', 'e', 'E', '+'].includes($event.key) && $event.preventDefault()"
              :disabled="isDisabled"
              placeholder="0"
            />
            <span class="mix-row__perc-symbol">%</span>
          </div>
          <button
            class="mix-row__remove"
            @click="handleRemove(produto.id)"
            :disabled="isDisabled"
            title="Remover produto"
          >
            <CIcon name="cil-x-circle" size="sm" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import { cdnUrlProduto } from "@/helpers/url";
import { mesmoProdutoId } from "@/helpers/campanhas";
import _debounce from "lodash/debounce";

export default {
  name: "ListaProdutos",
  props: {
    campanha: Object,
    itemsSelected: Array,
    selectCallback: Function,
    deselectCallback: Function,
    updatePercentageCallback: Function,
  },
  data() {
    return {
      searchTerm: "",
      searchResults: [],
      searchLoading: false,
    };
  },
  computed: {
    isDisabled() {
      return !!(this.campanha && this.campanha.erpId && this.campanha.id);
    },
  },
  created() {
    this.debounceSearch = _debounce(() => {
      this.searchProdutos();
    }, 300);
  },
  methods: {
    async searchProdutos() {
      if (!this.searchTerm || this.searchTerm.length < 2) {
        this.searchResults = [];
        return;
      }
      this.searchLoading = true;
      try {
        const json = await get(
          `/admin/campanhas-produtos-paginados?termo=${encodeURIComponent(this.searchTerm)}&take=20`
        );
        this.searchResults = json.itens || [];
      } catch (err) {
        console.error("Erro ao buscar produtos:", err);
        this.searchResults = [];
      } finally {
        this.searchLoading = false;
      }
    },

    isSelecionado(produtoId) {
      return this.itemsSelected.some((p) => mesmoProdutoId(p.id, produtoId));
    },

    toggleProduto(produto) {
      if (this.isDisabled) return;
      if (this.isSelecionado(produto.id)) {
        this.deselectCallback(produto.id);
      } else {
        this.selectCallback({
          id: produto.id,
          idExibicao: produto.idExibicao,
          desc: produto.descComp || produto.descNF || produto.desc,
          imagemId: produto.imagemId || null,
          percDesc: this.campanha.percDesc || 0,
        });
      }
    },

    handleRemove(id) {
      if (this.isDisabled) return;
      this.deselectCallback(id);
    },

    onPercentageChange(index, value) {
      if (this.updatePercentageCallback) {
        const perc = Math.max(0, parseFloat(value) || 0);
        this.updatePercentageCallback(index, perc);
      }
    },

    getImageUrl(produto) {
      const id = produto.imagemId || produto.id;
      return cdnUrlProduto(id);
    },
  },
};
</script>

<style scoped>
/* ===== LAYOUT ===== */
.mix-selector {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== SEARCH HEADER ===== */
.mix-search__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.search-wrapper {
  position: relative;
  flex: 0 0 300px;
}

.search-wrapper .search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #98a2b3;
}

.search-wrapper .form-control {
  padding-left: 36px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid #dddce9;
}

.search-wrapper .form-control:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.selected-counter {
  font-size: 13px;
  color: #667085;
  font-weight: 500;
  white-space: nowrap;
}

.selected-counter strong {
  color: #3a3e81;
}

/* ===== LOADING ===== */
.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #98a2b3;
  font-size: 13px;
}

/* ===== PRODUCT LIST (search results) ===== */
.produto-list {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  max-height: 350px;
  overflow-y: auto;
}

.produto-list-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.pl-check { flex: 0 0 40px; }
.pl-img { flex: 0 0 72px; }
.pl-info { flex: 1; }

.produto-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f2f4f7;
  cursor: pointer;
  transition: background 0.1s;
}

.produto-row:last-child { border-bottom: none; }
.produto-row:hover { background: #f9fafb; }

.produto-row.selected {
  background: #f2f2f8;
}

.produto-row .pl-check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #3a3e81;
}

.pl-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pl-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.pl-thumb .thumb-placeholder {
  color: #d0d5dd;
  font-size: 20px;
}

.pl-name {
  font-weight: 600;
  font-size: 13px;
  color: #1d2939;
  line-height: 1.3;
  margin-bottom: 2px;
}

.pl-id {
  font-size: 12px;
  color: #98a2b3;
}

/* ===== EMPTY STATES ===== */
.mix-empty {
  text-align: center;
  padding: 32px 20px;
  color: #98a2b3;
  border: 1px dashed #e4e7eb;
  border-radius: 10px;
}

.mix-empty .empty-icon {
  color: #d0d5dd;
  margin-bottom: 8px;
}

.mix-empty p {
  font-size: 13px;
  margin: 0;
}

/* ===== SELECTED MIX PRODUCTS ===== */
.mix-selected__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.mix-list {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.mix-list-header {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  gap: 12px;
}

.ml-thumb-col { flex: 0 0 44px; }
.ml-info-col { flex: 1; }
.ml-perc-col { flex: 0 0 110px; }
.ml-action-col { flex: 0 0 32px; }

.mix-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 12px;
  border-bottom: 1px solid #f2f4f7;
  transition: background 0.1s;
}

.mix-row:last-child { border-bottom: none; }
.mix-row:hover { background: #f9fafb; }

.mix-row__thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.mix-row__thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.mix-row__thumb .thumb-placeholder {
  color: #d0d5dd;
  font-size: 18px;
}

.mix-row__info {
  flex: 1;
  min-width: 0;
}

.mix-row__name {
  font-weight: 600;
  font-size: 13px;
  color: #1d2939;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mix-row__id {
  font-size: 12px;
  color: #98a2b3;
}

.mix-row__perc {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.mix-row__perc input {
  width: 72px;
  text-align: center;
  border: 1px solid #dddce9;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 13px;
  font-weight: 600;
}

.mix-row__perc input:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.mix-row__perc-symbol {
  font-size: 13px;
  color: #667085;
  font-weight: 600;
}

.mix-row__remove {
  background: none;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: #98a2b3;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.mix-row__remove:hover {
  border-color: #e74c3c;
  color: #e74c3c;
  background: #fef2f2;
}

.mix-row__remove:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
